import React, { useCallback, useMemo, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { type TFunction } from 'i18next';

import {
  CreateFolderRequestBody,
  FolderType,
  getGetOrganizationItemsQueryKey,
  getGetOrganizationStructureQueryKey,
  GetOrganizationItemsParams,
  useActivateFolder,
  useCreateFolderForOrganization,
  useGetFolder,
  useUpdateFolder
} from '@/api/generated';
import FolderBigIcon from '@/assets/icons/hierarchy-chart-folder.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import { useOrganizationStructure } from '@/hooks/useOrganizationStructure';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders } from '@/theme/constants';
import SettingsModalForm, { SettingsModalFormRef } from '@/views/SettingsModalForm/SettingsModalForm';

import CreateOrganizationItemModalContent from './CreateOrganizationItemModalContent';
import CreateOrganizationItemModalHeader from './CreateOrganizationItemModalHeader';
import FolderBasicInfoTab from './FolderBasicInfoTab';
import { getCreateFolderBasicTabSchema } from './schema';
import { CreateOrganizationItemFormData } from './types';

const getSteps = (t: TFunction) => [
  {
    content: FolderBasicInfoTab,
    title: t('layouts.organizations.create.tabs.basicInfo'),
    schema: getCreateFolderBasicTabSchema(t)
  }
  // {
  //   content: CommonUsersAccessTab,
  //   title: t('layouts.organizations.create.tabs.users'),
  //   schema: getEmptySchema()
  // }
];

const DEFAULT_VALUES = {
  name: ''
};

interface CreateFolderModalProps {
  isOpen?: boolean;
  onClose: () => void;
  currentFilter?: GetOrganizationItemsParams | null;
  type?: FolderType;
  locationSlug?: string | null;
  organizationSlug?: string;
  draftItemSlug?: string | null;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen = true,
  onClose,
  type,
  locationSlug: originalLocationSlug,
  organizationSlug,
  currentFilter,
  draftItemSlug
}) => {
  const { t } = useTranslation(['organizations', 'common', 'validation']);
  const { showMessage } = useSnackbar();
  const formRef = useRef<SettingsModalFormRef<FieldValues>>(null);
  const queryClient = useQueryClient();
  const locationSlug = originalLocationSlug || organizationSlug;

  const { data: draftItemData, isLoading: isFolderLoading } = useGetFolder(organizationSlug!, draftItemSlug!);

  const { mutateAsync: createFolderDraft, isLoading: isFolderCreating } = useCreateFolderForOrganization();
  const { mutateAsync: activateFolder, isLoading: isFolderActivating } = useActivateFolder();
  const { mutateAsync: updateFolder, isLoading: isFolderUpdating } = useUpdateFolder();

  const isLoading = isFolderCreating || isFolderActivating || isFolderUpdating || isFolderLoading;

  const handleFolderSave = useCallback(
    async (shouldActivate: boolean) => {
      const values = formRef?.current?.getValues() as CreateOrganizationItemFormData;
      const data: CreateFolderRequestBody = {
        name: values?.name,
        legacyId: values?.legacyId
        // TODO: Add assignments once ready on backend
        // assignments: values?.assignments ?? []
      };
      let itemSlugToActivate = draftItemSlug;
      if (!itemSlugToActivate) {
        data.parent = values?.parent;
        const result = await createFolderDraft({
          organizationSlug: organizationSlug!,
          data
        });

        await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
        await queryClient.invalidateQueries(getGetOrganizationStructureQueryKey(organizationSlug!));
        itemSlugToActivate = result?.slug;
      } else {
        await updateFolder({
          organizationSlug: organizationSlug!,
          folderSlug: itemSlugToActivate,
          data
        });
      }
      if (shouldActivate && itemSlugToActivate) {
        await activateFolder({
          organizationSlug: organizationSlug!,
          folderSlug: itemSlugToActivate
        });
      }
    },
    [activateFolder, createFolderDraft, currentFilter, draftItemSlug, organizationSlug, queryClient, updateFolder]
  );

  const handleSaveDraft = useCallback(async () => {
    try {
      await handleFolderSave(false);
      showMessage(t('layouts.organizations.create.draftSaved'));
      onClose();
      if (organizationSlug && currentFilter) {
        await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
      }
    } catch (error) {
      showMessage(t('layouts.organizations.create.alerts.error'));
    }
  }, [currentFilter, handleFolderSave, onClose, organizationSlug, queryClient, showMessage, t]);

  const handleActivate = useCallback(async () => {
    try {
      await handleFolderSave(true);
      showMessage(t('layouts.organizations.create.alerts.success'));

      if (organizationSlug && currentFilter) {
        await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
      }
    } catch (error) {
      showMessage(t('layouts.organizations.create.alerts.error'));
    }
  }, [currentFilter, handleFolderSave, organizationSlug, queryClient, showMessage, t]);

  const { mapStructure } = useOrganizationStructure();

  const parent = locationSlug && mapStructure?.[locationSlug];

  const defaultValues = useMemo(
    () =>
      parent &&
      (!draftItemSlug || draftItemData) && {
        ...(draftItemSlug
          ? {
              // FIXME: Uncomment once backend provides them
              name: draftItemData?.name
              // legacyId: draftItemData?.legacyId,
            }
          : DEFAULT_VALUES),
        organization: organizationSlug,
        parent: {
          slug: parent.slug,
          type: parent.type
        }
      },
    [draftItemData, draftItemSlug, organizationSlug, parent]
  );

  const steps = useMemo(() => getSteps(t), [t]);

  const schema = useMemo(() => getCreateFolderBasicTabSchema(t), [t]);

  if (!defaultValues) return <BackdropLoader />;

  return (
    <SettingsModalForm
      ref={formRef}
      isOpen={isOpen}
      onClose={onClose}
      defaultValues={defaultValues}
      onSave={handleActivate}
      isSaving={isLoading}
      validation={schema}
      head={
        <CreateOrganizationItemModalHeader
          Icon={FolderBigIcon}
          title={t('layouts.organizations.create.items.folder')}
          organizationSlug={organizationSlug}
          locationSlug={locationSlug}
          type={type}
        />
      }
      sx={{
        '&.CreateOrganizationModal-root .SettingsModalForm-root': {
          maxWidth: 600,
          minHeight: 'auto',
          height: 'auto'
        },
        '&.CreateOrganizationModal-root .MuiDialogTitle-root': {
          py: 0,
          borderBottom: borders.default,
          borderColor: 'grey.300'
        }
      }}
      className="CreateOrganizationModal-root"
    >
      <CreateOrganizationItemModalContent
        draftExists={!!draftItemSlug}
        handleSaveDraft={handleSaveDraft}
        isLoading={isLoading}
        steps={steps}
        title={t('layouts.organizations.create.items.folder')}
        draftAllowed
      />
    </SettingsModalForm>
  );
};

export default CreateFolderModal;
